import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider
} from '@mui/material';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Bank, Banks, CardsItemProps, Network, SpendingCategory } from '..';
import { List, TextField, ToggleSwitch } from '../../../../components/fields';
import { Regexes } from '../../../../definitions';
import { StateTopicEnum } from '../../../../enums';
import { publish, useStateReducer } from '../../../../hooks';
import { ICardsTemplate } from '../../../../interfaces';
import { getQueryParameter, validateObject } from '../../../../utilities';
import { parseFloatWithDecimal } from '../../../../utilities/stringUtils';
import AwardsRow from '../../../../_global/components/awards-row/AwardsRow';
import Heading from '../../../../_global/components/heading/Heading';
import { MainPrograms } from '../../../awards/components';
import { awardsDataService } from '../../../awards/services';
import { cardsDateService } from '../../services';
import { groupBy } from '../view-item/ViewItem';
import './_styles.scss';
import { UserPermissions } from '../../../../../src/definitions';
import { securityService } from '../../../../../src/_global/services';

export default function EditItem({
  data: {
    _id,
    name,
    signupBonus = 0,
    cashBonus = 0,
    minimumSpend = 0,
    daysToSpend = 0,
    annual_fee = 0,
    cashPerks = 0,
    cardFamily = 0,
    rewards_currency,
    bank,
    network,
    websiteUrl,
    referralCardLink,
    spendingCategories,
    mxGuid = { mx_cca_guid: '' },
    chargeCard = false,
    annualFeeWaived = false,
    bisCard = false,
    colour
  }
}: CardsItemProps) {
  const location = useLocation();
  const mode = getQueryParameter(location.search, 'mode');

  let listBanks: Banks[] = [];
  let listMainPrograms: MainPrograms[] = [];
  let listNetworks: Network[] = [];
  let listSpendingCate: SpendingCategory[] = [];
  let expanded: (keyof SpendingCategory)[] = [];
  const [state, setState] = useStateReducer({
    _id,
    card: {
      name,
      signupBonus,
      cashBonus,
      minimumSpend,
      daysToSpend,
      annual_fee,
      cashPerks,
      cardFamily,
      rewards_currency,
      bank,
      network,
      websiteUrl,
      referralCardLink,
      mxGuid,
      chargeCard,
      annualFeeWaived,
      bisCard,
      colour
    },
    spendingCategories,
    listSpendingCate,
    listBanks,
    listNetworks,
    listMainPrograms,
    addChild: false,
    mode,
    expanded
  });

  const [updatedState, setUpdatedState] = useStateReducer({
    _id
  });

  // Spending categoris grouping per mltiplier
  let groupedBy: SpendingCategory[] = groupBy(
    state.spendingCategories,
    'multiplier'
  );

  let spendingCategoriesKeys = Object.keys(groupedBy) as Array<
    keyof SpendingCategory
  >;

  spendingCategoriesKeys.sort(
    (a: string, b: string) => parseFloat(b) - parseFloat(a)
  );

  // Fecthing all spending categories
  React.useEffect(() => {
    state.expanded = [...spendingCategoriesKeys];
    state.expanded.pop();
    (async function fetchSpendCategories() {
      let s: SpendingCategory[] = await cardsDateService.getAllSpendCate();
      let b: Banks[] = await cardsDateService.getAllBanks();
      let n: Network[] = await cardsDateService.getAllNetworks();
      let p: MainPrograms[] = await awardsDataService.getMainPrograms();
      let rewardDisplayName = '';
      p.forEach((element: MainPrograms) => {
        if (element.main_program === state.rewards_currency)
          rewardDisplayName = element.display_program ?? '';
      });
      setState({
        listBanks: b,
        listSpendingCate: s?.map((e: SpendingCategory) => e.spend_category),
        listNetworks: n,
        listMainPrograms: p,
        rewardDisplayName: rewardDisplayName
      });
    })();
  }, []);

  const isValid = () => {
    if (errors['_id']) return false; // || updatedState["_id"]
    if (errors['name']) return false;
    if (errors['network']) return false;
    if (errors['bank']) return false;
    if (errors['rewards_currency']) return false;
    return true;
  };

  const errors = validateObject(
    {
      _id: {
        regex: Regexes.anyContentRegex,
        message: 'Card ID is required.'
      },
      'cards[`name`]': {
        regex: Regexes.anyContentRegex,
        message: 'Card Name is required.'
      },
      'cards[`network`]': {
        func: (val: Network) => {
          if (val && val.name) return true;

          return false;
        },
        message: 'Network Name is required.'
      },
      'cards[`bank`]': {
        func: (val: Bank) => {
          if (val && val.name) return true;

          return false;
        },
        message: 'Bank Name is required.'
      },
      'cards[`rewards_currency`]': {
        regex: Regexes.anyContentRegex,
        message: 'rewards_currency is required.'
      }
    },
    state
  );

  React.useEffect(() => {
    publish(StateTopicEnum.IsValid, isValid());
    publish(StateTopicEnum.EditItem, updatedState);
    // eslint-disable-next-line
  }, [updatedState]);

  const handleState = (key: string, value: any, handleMultiple: any = {}) => {
    if (Object.keys(handleMultiple).length !== 0) {
      setUpdatedState({ ...updatedState, ...handleMultiple });
      setState({ state, ...handleMultiple });
    } else {
      setUpdatedState({ ...updatedState, [key]: value });
      setState({ state, [key]: value });
    }
  };

  const handleCardState = (
    key: string,
    value: any,
    handleMultiple: any = {}
  ) => {
    if (Object.keys(handleMultiple).length !== 0) {
      setUpdatedState({
        ...updatedState,
        card: { ...updatedState.card, ...handleMultiple }
      });
      setState({ state, card: { ...state.card, ...handleMultiple } });
    } else {
      setUpdatedState({
        ...updatedState,
        card: { ...updatedState.card, [key]: value }
      });
      setState({ state, card: { ...state.card, [key]: value } });
    }
  };

  let mainp: MainPrograms = {
    main_program: state.rewards_currency ?? '',
    display_program: state.rewardDisplayName ?? ''
  };

  return (
    <div id='container-item' className='read-only'>
      <Heading name={state.card.name} style={{ margin: 0 }} />
      <div className='grid'>
        <TextField readOnly label='Identifier' value={state._id} />
      </div>

      <div className='grid'>
        <TextField
          error={errors['name'] ? true : false}
          label='Card Name'
          value={state.card.name}
          onChange={(e) => {
            handleCardState('name', e.target.value);
          }}
        />
        {securityService.checkRoleAccess(
          UserPermissions.viewDefaultCardLink
        ) ? (
          <TextField
            label='Default Card Link'
            value={state.card.websiteUrl}
            disabled={
              !securityService.checkRoleAccess(
                UserPermissions.editDefaultCardLink
              )
            }
            onChange={(e) => {
              handleCardState('websiteUrl', e.target.value);
            }}
          />
        ) : null}
      </div>
      {securityService.checkRoleAccess(UserPermissions.viewReferralCardLink) ? (
        <div className='grid'>
          <p></p>
          <TextField
            label='Referral Card Link'
            value={state.card.referralCardLink}
            disabled={
              !securityService.checkRoleAccess(
                UserPermissions.editReferralCardLink
              )
            }
            onChange={(e) => {
              handleCardState('referralCardLink', e.target.value);
            }}
          />
        </div>
      ) : null}
      <div className='grid'>
        <TextField
          label='Signup Bonus'
          value={state.card.signupBonus}
          type={'number'}
          onChange={(e) => {
            handleCardState('signupBonus', parseInt(e.target.value));
          }}
        />
        <TextField
          label='Cash Bonus'
          value={state.card.cashBonus}
          type={'number'}
          onChange={(e) => {
            handleCardState('cashBonus', parseInt(e.target.value));
          }}
        />
      </div>
      <div className='grid'>
        <TextField
          label='Minimum Spend'
          value={state.card.minimumSpend}
          type={'number'}
          onChange={(e) => {
            handleCardState('minimumSpend', parseInt(e.target.value));
          }}
        />
        <TextField
          label='Days To Spend'
          value={state.card.daysToSpend}
          type={'number'}
          onChange={(e) => {
            handleCardState('daysToSpend', parseInt(e.target.value));
          }}
        />
      </div>
      <div className='grid'>
        <TextField
          label='Annual Fee'
          value={state.card.annual_fee}
          type={'number'}
          onChange={(e) => {
            handleCardState('annual_fee', parseInt(e.target.value));
          }}
        />
        <TextField
          label='Cash Perks'
          value={state.card.cashPerks}
          type={'number'}
          onChange={(e) => {
            handleCardState('cashPerks', parseInt(e.target.value));
          }}
        />
      </div>
      <div className='grid'>
        <TextField
          label='Card Family'
          value={state.card.cardFamily}
          type={'number'}
          onChange={(e) => {
            handleCardState('cardFamily', parseInt(e.target.value));
          }}
        />
        <TextField
          label='Card Color'
          value={state.card.colour}
          type={'color'}
          onChange={(e) => {
            handleCardState('colour', e.target.value);
          }}
        />
      </div>

      <Divider />
      <div className='grid'>
        <ToggleSwitch
          value={state.card.annualFeeWaived}
          label={'Annual Fee Waived'}
          name={'annualFeeWaived'}
          onChange={(e) => {
            return handleCardState(e.target.name, e.target.checked);
          }}
        ></ToggleSwitch>
        <ToggleSwitch
          value={state.card.bisCard}
          label={'Business Card'}
          name={'bisCard'}
          onChange={(e) => {
            return handleCardState(e.target.name, e.target.checked);
          }}
        ></ToggleSwitch>
      </div>
      <div className='grid'>
        <ToggleSwitch
          value={state.card.chargeCard}
          label={'Charge Card'}
          name={'chargeCard'}
          onChange={(e) => {
            return handleCardState(e.target.name, e.target.checked);
          }}
        ></ToggleSwitch>
        <div></div>
      </div>

      <Divider />
      <div className='grid'>
        <List
          error={errors['network'] ? true : false}
          className='top-margin'
          label={`Network`}
          value={state.card.network}
          options={state.listNetworks}
          groupBy={(o: Network) => {
            return o.name.substring(0, 1);
          }}
          getOptionLabel={(o: Network) => {
            return `${o.name}`;
          }}
          getOptionValue={(o: Network) => {
            return `${o.name}`;
          }}
          onChange={(e, v) => {
            if (!v) return;

            handleCardState('network', v);
          }}
        />
        <List
          error={errors['bank'] ? true : false}
          className='top-margin'
          label={`Bank`}
          value={state.card.bank}
          options={state.listBanks}
          getOptionLabel={(o: Bank) => {
            return `${o.name}`;
          }}
          getOptionValue={(o: Bank) => {
            return `${o}`;
          }}
          groupBy={(o: Bank) => {
            return o.name.substring(0, 1);
          }}
          isOptionEqualToValue={(o: Bank, v: Bank) => {
            return `${o.name}` === `${v.name}`;
          }}
          onChange={(e, v) => {
            if (!v) return;

            handleCardState('bank', v);
          }}
        />
      </div>
      <div className='grid'>
        <List
          error={errors['rewards_currency'] ? true : false}
          className='top-margin'
          label={`Reward`}
          value={mainp}
          options={state.listMainPrograms}
          groupBy={(o: MainPrograms) => {
            return o.display_program?.substring(0, 1) ?? '';
          }}
          getOptionLabel={(o: MainPrograms) => {
            return o.display_program ?? '';
          }}
          getOptionValue={(o: MainPrograms) => {
            return o.display_program;
          }}
          isOptionEqualToValue={(o: MainPrograms, v: MainPrograms) => {
            return o.display_program === v.display_program;
          }}
          onChange={(e, v: MainPrograms) => {
            if (!v) return;
            handleCardState('rewards_currency', v.display_program);
          }}
        />
        <div />
      </div>
      <br />
      <Heading name='MX Guid' />
      <div className='grid'>
        <TextField
          label={'MX Guid'}
          value={state.card.mxGuid.mx_cca_guid}
          onChange={(e) => {
            handleCardState(
              'mxGuid.mx_cca_guid',
              state.card.mxGuid.mx_cca_guid
            );
          }}
        ></TextField>
        <div />
      </div>
      <Heading
        name='Spending Categories'
        onClickRight={() => {
          state.addChild = true;
          handleState('expanded', []);
        }}
      />
      {state.addChild ? (
        <AwardsRow
          readOnly={false}
          name={''}
          value={''}
          options={state.listSpendingCate ?? []}
          onDelete={() => {
            state.addChild = false;
            handleState('expanded', []);
          }}
          onChange={(name: string, value: string) => {
            state.spendingCategories = state.spendingCategories.find(
              (obj: SpendingCategory) => obj.spend_category !== name
            );

            let current = state.spendingCategories.find(
              (obj: SpendingCategory) => obj.spend_category === name
            );

            let o: SpendingCategory = {
              _id: current._id,
              card: _id,
              spend_category: name,
              multiplier: parseFloatWithDecimal(value, 2)
            };

            state.spendingCategories.push(o);
            handleState('spendingCategories', state.spendingCategories);

            state.addChild = false;
            handleState('expanded', []);
          }}
        />
      ) : (
        <div></div>
      )}
      {spendingCategoriesKeys &&
        spendingCategoriesKeys.map(
          (groupedMultiplier: keyof SpendingCategory, index: number) => {
            let intExpand: number = state.expanded.indexOf(groupedMultiplier);
            return (
              <Accordion
                expanded={intExpand >= 0}
                onChange={() => {
                  let te = state.expanded ?? [];
                  intExpand === -1
                    ? te.push(groupedMultiplier)
                    : te.splice(intExpand, 1);

                  handleState('expanded', te);
                }}
              >
                <AccordionSummary aria-controls='panel1d-content'>
                  <h2
                    style={{
                      color: 'white',
                      opacity: '0.8',
                      fontSize: '16px'
                    }}
                  >
                    {groupedMultiplier} x Multiplier
                  </h2>
                </AccordionSummary>
                <AccordionDetails>
                  {(state.spendingCategories &&
                    state.spendingCategories
                      ?.filter((element: SpendingCategory, index: number) => {
                        return (
                          parseFloat(groupedMultiplier) === element.multiplier
                        );
                      })
                      ?.map((val: SpendingCategory) => {
                        return (
                          <AwardsRow
                            key={val.spend_category ?? ''}
                            name={val.spend_category ?? ''}
                            value={val.multiplier.toString() ?? ''}
                            options={state.listSpendingCate ?? []}
                            onDelete={() => {
                              state.spendingCategories =
                                state.spendingCategories.filter(
                                  (obj: SpendingCategory) =>
                                    obj.spend_category !== val.spend_category
                                );

                              handleState(
                                'spendingCategories',
                                state.spendingCategories
                              );
                            }}
                            onChange={(name: string, value: string) => {
                              state.spendingCategories =
                                state.spendingCategories.filter(
                                  (obj: SpendingCategory) =>
                                    obj.spend_category !== val.spend_category
                                );

                              let o: SpendingCategory = {
                                _id: val._id,
                                card: _id,
                                spend_category: name,
                                multiplier: parseFloatWithDecimal(value, 2)
                              };

                              state.spendingCategories.push(o);

                              handleState(
                                'spendingCategories',
                                state.spendingCategories
                              );
                              handleState('expanded', []);
                            }}
                          />
                        );
                      })) ??
                    []}
                </AccordionDetails>
              </Accordion>
            );
          }
        )}
    </div>
  );
}
